import { EduQueryParameter } from '../../web/edu/eduConstants';
import { appContext } from '../../web/models/getHelp/appContext';
import { DafExecutingStatus } from '../../web/models/getHelp/DafExecutingStatus';
import { ExperienceMetaData } from '../../web/models/getHelp/experienceConfigurations';
import { GetHelpEcsData, initialGetHelpEcsStateData } from '../../web/models/getHelp/getHelpEcsData';
import { License } from '../../web/models/getHelp/license';
import { getMetaTagAttributeValue, getQueryParamDictionary, isGetHelpSourceApp, isThirdPartySourceApp } from '../../web/utils/statelessUtil';
import { SearchRequestsRequestModel } from '../packages/contactUs/models/contactUsModels';
import { RequestFeedbackSchema } from '../packages/contactUs/models/feedbackModels';
import { ModalityResponseModel, Region } from '../packages/contactUs/models/modalityModels';
import { FreelanceRequest, ErrorCodes } from '../packages/contactUs/models/requestModels';
import { ClientPromptForLoginState, getSourceAppEnum, isRequestCreationDeepLink } from '../utilities';
import { AlchemyResult, BcqrResult, UserInfo, AnswerDeskContentModel, ClientTokenRetrievalInfoModel, AuthInfo, AutoSuggestResult, LocalizedDaysOfWeek, AutoSuggestResultV2, BcqrExperimentType } from './apiModels';
import { Environment, URLs, SourceApps, OutlookDotComAppVer, AwaMetaTagType, ContentAttribute, isWindowsCopilotRequestCreationDeeplinkKey, getHelpDeepLinkQueryParameter, emeraldDeepLinkQueryParameter, isCMCRequestCreationDeeplinkKey } from './constants/constants';
export { createSelector, createStructuredSelector } from 'reselect';

// Shared States
export interface IAppState {
  GlobalState: IGlobalState;
  NavState: INavState;
  ContactState: IContactUsState;
}

export interface TenantInfo {
  dataBoundary: string[];
  isDfM: boolean;
  hasUnifiedContract: boolean;
  hasUnifiedEnterpriseContract: boolean;
  premierEntitlementId: string;
}

export interface IGlobalState {
  queryParams: { [key: string]: string };
  initializationComplete: boolean;
  socDataGrabComplete: boolean;
  isAddClientContextEnabled: boolean;
  environment: Environment;
  namespace: string;
  displayNamespace: string;
  productName: string;
  theme: string;
  accentColor: string;
  market: string;
  appVer: string;
  sourceApp: SourceApps;
  sessionData: ISessionData;
  version: string;
  tenantInfo: TenantInfo;
  isPremierTenantEnabled: boolean;
  licenses: License[];
  getHelpConfig: ExperienceMetaData;
  getHelpECS: GetHelpEcsData;
}

export class GlobalState implements IGlobalState {
  public queryParams: { [key: string]: string };
  public initializationComplete: boolean;
  public isAddClientContextEnabled: boolean;
  public environment: Environment;
  public namespace: string;
  public displayNamespace: string;
  public productName: string;
  public theme: string;
  public accentColor: string;
  public socDataGrabComplete: boolean;
  public market: string;
  public appVer: string;
  public sourceApp: SourceApps;
  public sessionData: ISessionData;
  public version: string;
  public tenantInfo: TenantInfo;
  public isPremierTenantEnabled: boolean;
  public licenses: License[];
  public getHelpConfig: ExperienceMetaData;
  public getHelpECS: GetHelpEcsData;
  public constructor() {
    this.market = null;
    this.theme = null;
    this.accentColor = null;
    this.displayNamespace = null;
    this.productName = null;
    this.namespace = null;
    this.initializationComplete = false;
    this.isAddClientContextEnabled = false;
    this.socDataGrabComplete = false;
    this.appVer = null;
    this.sourceApp = null;
    this.sessionData = null;
    this.version = null;
    this.tenantInfo = null;
    this.isPremierTenantEnabled = false;
    this.licenses = [];
    this.getHelpConfig = null;
    this.getHelpECS = initialGetHelpEcsStateData;
    this.queryParams = getQueryParamDictionary(window.location.href);
    const qpSourceApp = !!this.queryParams.sourceapp ? this.queryParams.sourceapp.toLowerCase() : '';
    const sourceAppFromConfig = window.emeraldContext ? window.emeraldContext.sourceApp : '';
    if (this.queryParams.isagave === 'true') {
      this.environment = Environment.agavePane;
    } else if (
      isThirdPartySourceApp(qpSourceApp)
    ) {
      this.environment = Environment.ThirdParty;
    } else if (isGetHelpSourceApp(sourceAppFromConfig)) {
      this.environment = Environment.GetHelp;
      this.theme = appContext.theme;
      this.accentColor = appContext.accentColor;
    } else if (window.location.href.toLowerCase().indexOf(OutlookDotComAppVer) !== -1) {
      // NOTE: This check *must* occur before the SOC check because, in the case of OWA and Emerald, /home/chat
      // is redirected to /home/contact which the SOC check is using.  This causes SOC to be rendered instead
      // of the correct OWA modalities page.
      this.environment = Environment.OutlookDotCom;
    } else if (
      window.location.href.indexOf(URLs.SOC) !== -1
      || window.location.href.indexOf(URLs.SMCExperiment) !== -1
      || window.location.href.indexOf(URLs.GroupMe) !== -1
      || window.location.href.indexOf(URLs.Edu) !== -1
    ) {
      this.environment = Environment.SOC;
    } else {
      this.environment = Environment.nonAgavePane;
    }
    if (this.queryParams.ns) {
      this.namespace = (this.queryParams.ns as string).toLowerCase();
    } else if (window.location.href.indexOf(URLs.GroupMe) !== -1) {
      this.sourceApp = SourceApps.GroupMe;
    } else if (window.location.href.indexOf(URLs.Edu) !== -1) {
      this.sourceApp = SourceApps.Edu;
    } else if (isGetHelpSourceApp(sourceAppFromConfig)) {
      this.sourceApp = getSourceAppEnum(sourceAppFromConfig);
    } else if (
      window.location.href.indexOf(URLs.SOC) !== -1
    ) {
      this.sourceApp = SourceApps.SOC2;
      // Get sourceApp from DOM and will be used to check if in rendering SMC content flight on SOC at L98
      const sourceAppFromDOM = getMetaTagAttributeValue(AwaMetaTagType.SourceApp, ContentAttribute);
      if (qpSourceApp === SourceApps.SignUp) {
        this.sourceApp = SourceApps.SignUp;
      } else if (qpSourceApp === SourceApps.Teams) {
        this.sourceApp = SourceApps.Teams;
      } else if (qpSourceApp === SourceApps.Teams2) {
        this.sourceApp = SourceApps.Teams2;
      } else if (qpSourceApp === SourceApps.TeamsAdmin) {
        this.sourceApp = SourceApps.TeamsAdmin;
      } else if (qpSourceApp === SourceApps.TeamsAdmin2) {
        this.sourceApp = SourceApps.TeamsAdmin2;
      } else if ((qpSourceApp === SourceApps.SMC2) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.SMC2)) {
        this.sourceApp = SourceApps.SMC2;
      } else if ((qpSourceApp === SourceApps.SurfaceIVR) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.SurfaceIVR)) {
        this.sourceApp = SourceApps.SurfaceIVR;
      } else if ((qpSourceApp === SourceApps.SurfaceIVR2) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.SurfaceIVR2)) {
        this.sourceApp = SourceApps.SurfaceIVR2;
      } else if ((qpSourceApp === SourceApps.SMCIVR) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.SMCIVR)) {
        this.sourceApp = SourceApps.SMCIVR;
      } else if ((qpSourceApp === SourceApps.SMCIVR2) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.SMCIVR2)) {
        this.sourceApp = SourceApps.SMCIVR2;
      } else if (qpSourceApp === SourceApps.Proactive) {
        this.sourceApp = SourceApps.Proactive;
      } else if (
        (qpSourceApp === SourceApps.SurfaceApp) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.SurfaceApp) ||
        (qpSourceApp === SourceApps.SurfaceApp2) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.SurfaceApp2)
      ) {
        this.sourceApp = SourceApps.SurfaceApp;
      } else if ((qpSourceApp === SourceApps.SurfaceWeb) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.SurfaceWeb)) {
        this.sourceApp = SourceApps.SurfaceWeb;
      } else if ((qpSourceApp === SourceApps.LightHouse) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.LightHouse)) {
        this.sourceApp = SourceApps.LightHouse;
      } else if ((qpSourceApp === SourceApps.AMC) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.AMC)) {
        this.sourceApp = SourceApps.AMC;
      } else if ((qpSourceApp === SourceApps.OfficeDotCom) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.OfficeDotCom)) {
        this.sourceApp = SourceApps.OfficeDotCom;
      } else if ((qpSourceApp === SourceApps.OutlookAdmin) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.OutlookAdmin)) {
        this.sourceApp = SourceApps.OutlookAdmin;
      } else if ((qpSourceApp === SourceApps.OneDriveWeb) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.OneDriveWeb)) {
        this.sourceApp = SourceApps.OneDriveWeb;
      } else if ((qpSourceApp === SourceApps.W365C1) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.W365C1)) {
        this.sourceApp = SourceApps.W365C1;
      } else if ((qpSourceApp === SourceApps.SmbHub1) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.SmbHub1)) {
        this.sourceApp = SourceApps.SmbHub1;
      } else if ((qpSourceApp === SourceApps.Edu) ||
        (!!sourceAppFromDOM && sourceAppFromDOM.toLowerCase() === SourceApps.Edu)) {
        this.sourceApp = SourceApps.Edu;
      } else if (isThirdPartySourceApp(qpSourceApp)) {
        this.sourceApp = getSourceAppEnum(qpSourceApp);
      }
    } else if (
      window.location.href.indexOf(URLs.SMCExperiment) !== -1
    ) {
      this.sourceApp = SourceApps.SMCExp;
      if (qpSourceApp === SourceApps.SMCIVR) {
        this.sourceApp = SourceApps.SMCIVR;
      } else if (qpSourceApp === SourceApps.SurfaceIVR) {
        this.sourceApp = SourceApps.SurfaceIVR;
      }
    }
  }
}

export enum UITabViewMode {
  edit = 1,
  readOnly = 2
}

export enum UITabView {
  searchHelp = 'searchHelp',
  requestCreation = 'requestCreation',
  requestHistory = 'requestHistory',
  requestDetails = 'requestDetails',
  articleReader = 'articleReader'
}

export interface IUITabViewInfo {
  uITabView: UITabView;
  requestId?: string;
  viewMode: UITabViewMode;
  // Below property is the url for the use of articleReader, opening url in iframe
  url?: string;
  // BackButton on the AppBar, will be true if gethelp and if page needs back button. Not all pages need to display back button
  showAppBarBackButton?: boolean;
}

export class UITabViewInfo implements IUITabViewInfo {
  public uITabView!: UITabView;
  public requestId!: string;
  public viewMode: UITabViewMode;
  public url?: string;
  public showAppBarBackButton?: boolean;

  public constructor() {
    this.uITabView = UITabView.searchHelp;
    this.viewMode = UITabViewMode.readOnly;
    this.url = '';
    this.showAppBarBackButton = false;
  }
}

export const initialGlobalState = new GlobalState();

export interface INavState {
  history: string[];
  backButtonHit: boolean;
  backButtonDisabled: boolean;
  requestCreationTabHidden: boolean;
  twoClickSupportExp: boolean;
  inContactArticle: boolean;
  contactIframeSrc: string;
  userSearched: boolean;
  uITabViewStack: IUITabViewInfo[];
}

export class NavState implements INavState {
  public history: string[];
  public backButtonHit: boolean;
  public backButtonDisabled: boolean;
  public requestCreationTabHidden: boolean;
  public twoClickSupportExp: boolean;
  public inContactArticle: boolean;
  public contactIframeSrc: string;
  public userSearched: boolean;
  public uITabViewStack: IUITabViewInfo[];

  public constructor() {
    this.twoClickSupportExp = true;
    this.backButtonHit = false;
    this.history = [];
    this.backButtonDisabled = true;
    this.requestCreationTabHidden = false;
    this.userSearched = false;
    this.contactIframeSrc = null;
    this.inContactArticle = false;
    this.uITabViewStack = [new UITabViewInfo()];
  }
}

export const initialNavState = new NavState();

// Contact Us States

export interface ISharedState {
  isSMCExperiment: boolean;
  requestVerificationToken: string;
  contactSearchedQuery: string;
  clientPromptForLoginState: ClientPromptForLoginState;
  isUserNoticeEnabled: boolean;
  userNoticeAlchemyQuery: string;
  isUnAuthSupportEnabled: boolean;
  safActionMaxRetries?: number;
  deeplinkPath: string;
  isDeepLinkEnabled: boolean;
  isDeepLinkQuery: boolean;
  hideSelfHelpSmcIvr: boolean;
  isOutlookDotCom: boolean;
  showSupportNav: boolean;
  useIconsForSupportNav: boolean;
  isAutoSuggestCardSearch: boolean;
  isSearchSessionResetEnabled: boolean;
  shouldShowOutdatedDevicesAlert: boolean;
  shouldShowVsbUpsellAlert: boolean;
  deviceMinCountToShowExpandIcon: number;
  deviceMaxCount: number;
  howToUpdateWindowsUrl: string;
  windowsUpdateInvokingUrl: string;
  compareM365PlansUrl: string;
  regionInfo: Array<Region>;
  openLinksInNewTab: boolean;
  isLogBundleLoadSuccessEnabled: boolean;
  useAnnouncementFloat: boolean;
  shouldResetFocus: boolean;
  isBusinessAssistUXEnabled: boolean;
  isFeedbackTicketCreation: boolean;
  isFeedbackCoachmarkShowing: boolean;
  isFeedbackCoachmarkEnabled: boolean;
  isWindowsCopilotRequestCreationDeeplink: boolean;
  isCMCRequestCreationDeeplink: boolean;
  isPostAlchemyFeedbackEnabled: boolean;
  showWin10EndOfSupportMessage: boolean;
}

export class SharedState implements ISharedState {
  public isSMCExperiment: boolean;
  public requestVerificationToken: string;
  public contactSearchedQuery: string;
  public clientPromptForLoginState: ClientPromptForLoginState;
  public isUserNoticeEnabled: boolean;
  public userNoticeAlchemyQuery: string;
  public isUnAuthSupportEnabled: boolean;
  public safActionMaxRetries!: number;
  public isDeepLinkEnabled: boolean;
  public isDeepLinkQuery: boolean;
  public hideSelfHelpSmcIvr: boolean;
  public isOutlookDotCom: boolean;
  public showSupportNav: boolean;
  public useIconsForSupportNav: boolean;
  public isAutoSuggestCardSearch: boolean;
  public isSearchSessionResetEnabled: boolean;
  public shouldShowOutdatedDevicesAlert: boolean;
  public shouldShowVsbUpsellAlert: boolean;
  public deviceMinCountToShowExpandIcon: number;
  public deviceMaxCount: number;
  public howToUpdateWindowsUrl: string;
  public windowsUpdateInvokingUrl: string;
  public compareM365PlansUrl: string;
  public regionInfo: Array<Region>;
  public openLinksInNewTab: boolean;
  public deeplinkPath: string;
  public isLogBundleLoadSuccessEnabled: boolean;
  public useAnnouncementFloat: boolean;
  public shouldResetFocus: boolean;
  public isBusinessAssistUXEnabled: boolean;
  public isFeedbackTicketCreation: boolean;
  public isFeedbackCoachmarkShowing: boolean;
  public isFeedbackCoachmarkEnabled: boolean;
  public isWindowsCopilotRequestCreationDeeplink: boolean;
  public isCMCRequestCreationDeeplink: boolean;
  public isPostAlchemyFeedbackEnabled: boolean;
  public showWin10EndOfSupportMessage: boolean;

  public constructor() {
    const rvfElement = document.getElementsByName('__RequestVerificationToken');
    const requestVerificationToken = !!rvfElement && !!rvfElement[0] ? (rvfElement[0] as HTMLInputElement).value : '';
    this.isOutlookDotCom = window.location.href.toLowerCase().indexOf(OutlookDotComAppVer) !== -1;
    this.hideSelfHelpSmcIvr = false;
    this.isDeepLinkEnabled = false;
    this.isDeepLinkQuery = false;
    this.isUserNoticeEnabled = false;
    this.userNoticeAlchemyQuery = '';
    this.isUnAuthSupportEnabled = false;
    this.isSMCExperiment = window.location.href.indexOf(URLs.SMCExperiment) !== -1;
    this.requestVerificationToken = requestVerificationToken;
    this.contactSearchedQuery = '';
    this.clientPromptForLoginState = ClientPromptForLoginState.FAILED_OR_CANCELLED;
    this.safActionMaxRetries = 0;
    this.showSupportNav = false;
    this.useIconsForSupportNav = false;
    this.isAutoSuggestCardSearch = false;
    this.isSearchSessionResetEnabled = false;
    this.shouldShowOutdatedDevicesAlert = false;
    this.shouldShowVsbUpsellAlert = false;
    this.deviceMinCountToShowExpandIcon = 3;
    this.deviceMaxCount = 10;
    this.howToUpdateWindowsUrl = '';
    this.windowsUpdateInvokingUrl = '';
    this.compareM365PlansUrl = '';
    this.regionInfo = null;
    this.openLinksInNewTab = false;
    const sourceApp = (window.emeraldContext && window.emeraldContext.sourceApp) || '';
    this.deeplinkPath = isGetHelpSourceApp(sourceApp) ? appContext.deeplinkPath : '';
    this.isLogBundleLoadSuccessEnabled = false;
    this.useAnnouncementFloat = false;
    this.shouldResetFocus = false;
    this.isBusinessAssistUXEnabled = false;
    this.isFeedbackTicketCreation = false;
    this.isFeedbackCoachmarkShowing = false;
    this.isFeedbackCoachmarkEnabled = false;
    this.isWindowsCopilotRequestCreationDeeplink = isGetHelpSourceApp(sourceApp) && isRequestCreationDeepLink(appContext.deeplinkPath, getHelpDeepLinkQueryParameter + isWindowsCopilotRequestCreationDeeplinkKey);
    const sourceAppFromDOM = getMetaTagAttributeValue(AwaMetaTagType.SourceApp, ContentAttribute);
    this.isCMCRequestCreationDeeplink = sourceAppFromDOM?.toLowerCase() === SourceApps.SMC2 && isRequestCreationDeepLink(window.location.href, emeraldDeepLinkQueryParameter + isCMCRequestCreationDeeplinkKey);
    this.isPostAlchemyFeedbackEnabled = false;
    this.showWin10EndOfSupportMessage = false;
  }
}

export const initialSharedState: ISharedState = new SharedState();

export interface IContactUsState {
  SharedState: ISharedState;
  AutoSuggestState: IAutoSuggestState;
  SelfHelpAlchemyState: ISelfHelpAlchemyState;
  ModalitySectionState: IModalitySectionState;
  UserNoticeState: IUserNoticeState;
  RequestDetailsState: IRequestDetailsState;
  RequestHistoryState: IRequestHistoryState;
  AttachmentsState: IAttachmentsState;
  OcvState: IOcvState;
  RequestFeedbackState: IRequestFeedbackState;
  DafState: IDafState;
}

export interface IDafState {
  nodeId: string;
  packageName: string;
  startupDiagResult: Record<string, string>;
  startupDiagStatus: DafExecutingStatus;
}

export const initialDafState: IDafState = {
  nodeId: '',
  packageName: '',
  startupDiagResult: {},
  startupDiagStatus: DafExecutingStatus.NotStarted,
};

export interface IUserNotice {
  noticeId: string;
  noticeText: string;
  isDismissed: boolean;
}

export class UserNotice implements IUserNotice {
  public noticeId: string;
  public noticeText: string;
  public isDismissed: boolean;
  public constructor(noticeId: string, noticeText: string) {
    this.noticeId = noticeId;
    this.noticeText = noticeText;
    this.isDismissed = false;
  }
}

export interface IUserNoticeState {
  userNotices: IUserNotice[];
}

export class UserNoticeState implements IUserNoticeState {
  public userNotices: IUserNotice[];
  public constructor() {
    this.userNotices = [];
  }
}

export const initialUserNoticeState = new UserNoticeState();

export interface IAutoSuggestState {
  autoSuggestResults: AutoSuggestResult[];
  autoSuggestCardResults: AutoSuggestResult[];
  autoSuggestResultsV2: AutoSuggestResultV2;
  autoSuggestCardResultsV2: AutoSuggestResultV2;
  isAutoSuggestEnabled: boolean;
  isAutoSuggestV2Enabled: boolean;
  isAutoSuggestHidden: boolean;
  isAutoSuggestQuery: boolean;
  autoSuggestFocusedIndex: number;
  originalQuery: string;
  autoSuggestBaseUrl: string;
  autoSuggestBaseUrlV2: string;
}

export interface ISelfHelpBcqrState {
  bcqrResults: BcqrResult[];
  isBcqrSearchInProgress: boolean;
  inBcqrResults: boolean;
  isCollapsedSelfHelpBcqr: boolean;
}

export interface ISelfHelpAlchemyState {
  alchemyResults: AlchemyResult;
  isAlchemyQueryInProgress: boolean;
  isAlchemyQueryCompleted: boolean;
  areSolutionsPresent: boolean;
  areInsightsPresent: boolean;
  areAIInsightsPresent: boolean;
  isAlchmeySolutionsEnabled: boolean;
  isAlchmeyInsightsEnabled: boolean;
  isMultiInsightsEnabled: boolean;
  isInsightActionButtonEnabled: boolean;
  isJumpToFirstLinkEnabled: boolean;
  insightFeedbackSubmited: boolean;
  disambiguationBranchingEnabled: boolean;
  showModalitiesStickyFooter: boolean;
  showRecommendedArticlesHeader: boolean;
  showAutoSuggestCard: boolean;
  relatedSHCorrelationIds: string;
  isOutdatedDevicesAlertDismissed: boolean;
  isVsbUpsellAlertDismissed: boolean;
  isVsbUpsellAlertCTAClicked: boolean;
  isVsbUpsellAlertActivated: boolean;
  shouldExpandOutdatedDevicesAlert: boolean;
  bcqrExperimentType: BcqrExperimentType;
  isTopResultForSelfHelpEnabled: boolean;
  hideGetHelpBtn: boolean;
  hidePrivacyAndSupport: boolean;
  showBCQRV2: boolean;
  dialogId: string;
  showMoreInsightsExpanded: boolean;
  isQueryChanged: boolean;
  isWindowsUpdateAvailableForSA: boolean;
  isEndOfLifeReachedForSA: boolean;
}

export enum ServiceRequestPageType {
  SignInSection,
  AlchemyThemeRouting,
  ModalityOptions,
  RequestCreation,
  RequestConfirmation
}

export type ServiceRequestPage = {
  isBackButtonPresent: boolean;
  serviceRequestPageType: ServiceRequestPageType;
};

export interface IModalitySectionState {
  consolidateGetModalitiesAPI: boolean;
  isGettingAvailableModalities: boolean;
  alchemyThemeRoutingEnabled: boolean;
  alchemyThemeRoutingShowWaitTime: boolean;
  alchemyThemeRoutingShowHOOPs: boolean;
  isAMCModalityEnabled: boolean;
  isRenderingModalitySection: boolean;
  inModalities: boolean;
  isModalitiesSearchInProgress: boolean;
  isCollapsedModalities: boolean;
  redirectForSignInLink: string;
  answerDeskContentModel: AnswerDeskContentModel;
  isSignedIn: boolean;
  userInfo: UserInfo;
  isOrgId: boolean;
  isAdmin: boolean;
  isMSFT: boolean;
  backgroundAuthUri: string;
  hasThirdPartyCookiesEnabled: boolean;
  clientTokenRetrievalInfoModel: ClientTokenRetrievalInfoModel;
  isUserIsSigningIntoClientApp: boolean;
  errorOnSignIn: boolean;
  authInfo: AuthInfo;
  alchemyUnsupportedThemes: string[];
  localizedDaysOfWeek: LocalizedDaysOfWeek;
  isUnauthSchemaFetching: boolean;
  unAuthSignInResult: AlchemyResult;
  showUnAuthBranching: boolean;
  SMCUnauthExperienceAlchemyAppName: string;
  unAuthBranchingEnabled: boolean;
  modalityResponse: ModalityResponseModel;
  draftRequest: FreelanceRequest;
  isC2CCountryCodeEnabled: boolean;
  isPhoneNumberValid: boolean;
  isEmailValid: boolean;
  isTitleValid: boolean;
  isCreatingRequest: boolean;
  createdRequest: FreelanceRequest;
  hideChatOutlookDotCom: boolean;
  enableEmailRemovalOutlookDotCom: boolean;
  autoOpenRaveChatRequestCorrelationId: string;
  userInfoFetchEnded: boolean;
  isModalityThrottled: boolean;
  userImpactOptions: string[];
  userRoleOptions: string[];
  isUserImpactAndRoleDropdownEnabled: boolean;
  isOutdatedDevicesAlertDismissed: boolean;
  isVsbUpsellAlertDismissed: boolean;
  isVsbUpsellAlertCTAClicked: boolean;
  isVsbUpsellAlertActivated: boolean;
  shouldExpandOutdatedDevicesAlert: boolean;
  isLiveChatEmailRequirementDisabled: boolean;
  businessImpactConfirmed: boolean;
  customerAvailabilityConfirmed: boolean;
  isRegionalSettingsEnabled: boolean;
  showAgentModalitiesButtonClicked: boolean;
  timeZones: [];
  supportedLanguages: [];
  preferredTimeZone: string;
  preferredLocale: string;
  serviceRequestPages: ServiceRequestPage[];
  isDfC: boolean;
  openInAppOmnichannelChatInBrowser: boolean;
  eduProductId: string;
  isWindowsUpdateAvailableForSA: boolean;
  isEndOfLifeReachedForSA: boolean;
}

export class AutoSuggestState implements IAutoSuggestState {
  public autoSuggestResults: AutoSuggestResult[];
  public autoSuggestCardResults: AutoSuggestResult[];
  public autoSuggestResultsV2: AutoSuggestResultV2;
  public autoSuggestCardResultsV2: AutoSuggestResultV2;
  public isAutoSuggestEnabled: boolean;
  public isAutoSuggestV2Enabled: boolean;
  public isAutoSuggestHidden: boolean;
  public isAutoSuggestQuery: boolean;
  public autoSuggestFocusedIndex: number;
  public originalQuery: string;
  public autoSuggestBaseUrl: string;
  public autoSuggestBaseUrlV2: string;
  public constructor() {
    this.autoSuggestResults = null;
    this.autoSuggestCardResults = null;
    this.autoSuggestResultsV2 = null;
    this.autoSuggestCardResultsV2 = null;
    this.isAutoSuggestEnabled = false;
    this.isAutoSuggestV2Enabled = false;
    this.isAutoSuggestHidden = true;
    this.isAutoSuggestQuery = false;
    this.autoSuggestFocusedIndex = -1;
    this.originalQuery = '';
    this.autoSuggestBaseUrl = null;
    this.autoSuggestBaseUrlV2 = null;
  }
}

export const initialAutoSuggestState: IAutoSuggestState = new AutoSuggestState();

export class SelfHelpAlchemyState implements ISelfHelpAlchemyState {
  public alchemyResults: AlchemyResult;
  public isAlchemyQueryInProgress: boolean;
  public isAlchemyQueryCompleted: boolean;
  public areSolutionsPresent: boolean;
  public areInsightsPresent: boolean;
  public areAIInsightsPresent: boolean;
  public isAlchmeySolutionsEnabled: boolean;
  public isAlchmeyInsightsEnabled: boolean;
  public isMultiInsightsEnabled: boolean;
  public isInsightActionButtonEnabled: boolean;
  public isJumpToFirstLinkEnabled: boolean;
  public insightFeedbackSubmited: boolean;
  public disambiguationBranchingEnabled: boolean;
  public showModalitiesStickyFooter: boolean;
  public showRecommendedArticlesHeader: boolean;
  public showAutoSuggestCard: boolean;
  public relatedSHCorrelationIds: string;
  public isOutdatedDevicesAlertDismissed: boolean;
  public isVsbUpsellAlertDismissed: boolean;
  public isVsbUpsellAlertCTAClicked: boolean;
  public isVsbUpsellAlertActivated: boolean;
  public shouldExpandOutdatedDevicesAlert: boolean;
  public bcqrExperimentType: BcqrExperimentType;
  public isTopResultForSelfHelpEnabled: boolean;
  public hideGetHelpBtn: boolean;
  public hidePrivacyAndSupport: boolean;
  public showBCQRV2: boolean;
  public dialogId: string;
  public showMoreInsightsExpanded: boolean;
  public isQueryChanged: boolean;
  public isWindowsUpdateAvailableForSA: boolean;
  public isEndOfLifeReachedForSA: boolean;
  public constructor() {
    this.alchemyResults = null;
    this.isAlchemyQueryInProgress = false;
    this.isAlchemyQueryCompleted = false;
    this.areSolutionsPresent = false;
    this.areInsightsPresent = false;
    this.areAIInsightsPresent = false;
    this.isAlchmeySolutionsEnabled = false;
    this.isAlchmeyInsightsEnabled = false;
    this.isMultiInsightsEnabled = false;
    this.isInsightActionButtonEnabled = false;
    this.isJumpToFirstLinkEnabled = true;
    this.insightFeedbackSubmited = false;
    this.disambiguationBranchingEnabled = false;
    this.showModalitiesStickyFooter = false;
    this.showAutoSuggestCard = false;
    this.showRecommendedArticlesHeader = false;
    this.relatedSHCorrelationIds = '';
    this.isOutdatedDevicesAlertDismissed = false;
    this.isVsbUpsellAlertDismissed = false;
    this.isVsbUpsellAlertCTAClicked = false;
    this.isVsbUpsellAlertActivated = false;
    this.shouldExpandOutdatedDevicesAlert = false;
    this.bcqrExperimentType = BcqrExperimentType.Control;
    this.isTopResultForSelfHelpEnabled = false;
    this.hideGetHelpBtn = false;
    this.hidePrivacyAndSupport = false;
    this.showBCQRV2 = false;
    this.dialogId = '';
    this.showMoreInsightsExpanded = false;
    this.isQueryChanged = false;
    this.isWindowsUpdateAvailableForSA = false;
    this.isEndOfLifeReachedForSA = false;
  }
}

export const initialSelfHelpAlchemyState: ISelfHelpAlchemyState = new SelfHelpAlchemyState();

export class ModalitySectionState implements IModalitySectionState {
  public consolidateGetModalitiesAPI: boolean;
  public isGettingAvailableModalities: boolean;
  public alchemyThemeRoutingEnabled: boolean;
  public alchemyThemeRoutingShowWaitTime: boolean;
  public alchemyThemeRoutingShowHOOPs: boolean;
  public isAMCModalityEnabled: boolean;
  public isRenderingModalitySection: boolean;
  public inModalities: boolean;
  public isModalitiesSearchInProgress: boolean;
  public isCollapsedModalities: boolean;
  public redirectForSignInLink: string;
  public answerDeskContentModel: AnswerDeskContentModel;
  public isSignedIn: boolean;
  public userInfo: UserInfo;
  public isOrgId: boolean;
  public isAdmin: boolean;
  public isMSFT: boolean;
  public backgroundAuthUri: string;
  public hasThirdPartyCookiesEnabled: boolean;
  public clientTokenRetrievalInfoModel: ClientTokenRetrievalInfoModel;
  public isUserIsSigningIntoClientApp: boolean = false;
  public errorOnSignIn: boolean = false;
  public authInfo: AuthInfo;
  public alchemyUnsupportedThemes: string[];
  public localizedDaysOfWeek: LocalizedDaysOfWeek;
  public isUnauthSchemaFetching: boolean;
  public unAuthSignInResult: AlchemyResult;
  public showUnAuthBranching: boolean;
  public SMCUnauthExperienceAlchemyAppName: string;
  public unAuthBranchingEnabled: boolean;
  public modalityResponse: ModalityResponseModel;
  public draftRequest: FreelanceRequest;
  public isC2CCountryCodeEnabled: boolean;
  public isPhoneNumberValid: boolean;
  public isEmailValid: boolean;
  public isTitleValid: boolean;
  public isCreatingRequest: boolean;
  public createdRequest: FreelanceRequest;
  public hideChatOutlookDotCom: boolean;
  public enableEmailRemovalOutlookDotCom: boolean;
  public autoOpenRaveChatRequestCorrelationId: string;
  public userInfoFetchEnded: boolean;
  public isModalityThrottled: boolean;
  public userImpactOptions: string[];
  public userRoleOptions: string[];
  public isUserImpactAndRoleDropdownEnabled: boolean;
  public isOutdatedDevicesAlertDismissed: boolean;
  public isWindowsUpdateAvailableForSA: boolean;
  public isEndOfLifeReachedForSA: boolean;
  public isVsbUpsellAlertDismissed: boolean;
  public isVsbUpsellAlertCTAClicked: boolean;
  public isVsbUpsellAlertActivated: boolean;
  public shouldExpandOutdatedDevicesAlert: boolean;
  public isLiveChatEmailRequirementDisabled: boolean;
  public businessImpactConfirmed: boolean;
  public customerAvailabilityConfirmed: boolean;
  public isRegionalSettingsEnabled: boolean;
  public showAgentModalitiesButtonClicked: boolean;
  public timeZones: [];
  public supportedLanguages: [];
  public preferredTimeZone: string;
  public preferredLocale: string;
  public serviceRequestPages: ServiceRequestPage[];
  public isDfC: boolean;
  public openInAppOmnichannelChatInBrowser: boolean;
  public eduProductId: string;
  public constructor() {
    this.userInfoFetchEnded = false;
    this.consolidateGetModalitiesAPI = false;
    this.isGettingAvailableModalities = false;
    this.alchemyThemeRoutingEnabled = false;
    this.alchemyThemeRoutingShowWaitTime = false;
    this.alchemyThemeRoutingShowHOOPs = false;
    this.isAMCModalityEnabled = false;
    this.isRenderingModalitySection = false;
    this.redirectForSignInLink = '';
    this.isSignedIn = false;
    this.isOrgId = false;
    this.isAdmin = false;
    this.isMSFT = false;
    this.inModalities = false;
    this.isModalitiesSearchInProgress = false;
    this.answerDeskContentModel = null;
    this.isCollapsedModalities = false;
    this.userInfo = null;
    this.backgroundAuthUri = '';
    this.hasThirdPartyCookiesEnabled = true;
    this.clientTokenRetrievalInfoModel = null;
    this.authInfo = null;
    this.alchemyUnsupportedThemes = [];
    this.localizedDaysOfWeek = null;
    this.isUnauthSchemaFetching = false;
    this.unAuthSignInResult = null;
    this.showUnAuthBranching = false;
    this.SMCUnauthExperienceAlchemyAppName = 'smcunauth';
    this.unAuthBranchingEnabled = false;
    this.modalityResponse = null;
    this.draftRequest = new FreelanceRequest();
    this.isC2CCountryCodeEnabled = false;
    this.isPhoneNumberValid = true;
    this.isEmailValid = true;
    this.isTitleValid = false;
    this.isCreatingRequest = false;
    this.createdRequest = null;
    this.hideChatOutlookDotCom = false;
    this.enableEmailRemovalOutlookDotCom = false;
    this.autoOpenRaveChatRequestCorrelationId = '';
    this.isModalityThrottled = false;
    this.userImpactOptions = [];
    this.userRoleOptions = [];
    this.isUserImpactAndRoleDropdownEnabled = false;
    this.isOutdatedDevicesAlertDismissed = false;
    this.isWindowsUpdateAvailableForSA = false;
    this.isEndOfLifeReachedForSA = false;
    this.isVsbUpsellAlertDismissed = false;
    this.isVsbUpsellAlertCTAClicked = false;
    this.isVsbUpsellAlertActivated = false;
    this.shouldExpandOutdatedDevicesAlert = false;
    this.isLiveChatEmailRequirementDisabled = false;
    this.businessImpactConfirmed = false;
    this.customerAvailabilityConfirmed = false;
    this.isRegionalSettingsEnabled = false;
    this.showAgentModalitiesButtonClicked = false;
    this.timeZones = [];
    this.supportedLanguages = [];
    this.preferredTimeZone = '';
    this.preferredLocale = '';
    this.serviceRequestPages = [];
    this.isDfC = false;
    this.openInAppOmnichannelChatInBrowser = false;
    const queryParams = getQueryParamDictionary(window.location.href);
    this.eduProductId = queryParams[EduQueryParameter.ProductId];
  }
}

export const initialModalitySectionState: IModalitySectionState = new ModalitySectionState();

export interface IRequestDetailsState {
  dirtyRequest: FreelanceRequest;
  isEmailValid: boolean;
  isTitleValid: boolean;
  isPhoneNumberValid: boolean;
  isUpdatingAttachments: boolean;
  isUpdateAttachmentsError: boolean;
  isAddingNote: boolean;
  isAddNoteError: boolean;
  isUpdatingContacts: boolean;
  isUpdateContactsError: boolean;
  isRequestEditingEnabled: boolean;
  addNoteConfig: AddNoteConfig;
  isC2CPhoneEditingEnabled: boolean;
  isRequestDetailsPhoneEditEnabled: boolean;
  businessImpactConfirmed: boolean;
  customerAvailabilityConfirmed: boolean;
  isGetRequestPending: boolean;
}

export class RequestDetailsState implements IRequestDetailsState {
  public dirtyRequest: FreelanceRequest;
  public isEmailValid: boolean;
  public isTitleValid: boolean;
  public isPhoneNumberValid: boolean;
  public isUpdatingAttachments: boolean;
  public isUpdateAttachmentsError: boolean;
  public isAddingNote: boolean;
  public isAddNoteError: boolean;
  public isUpdatingContacts: boolean;
  public isUpdateContactsError: boolean;
  public isRequestEditingEnabled: boolean;
  public addNoteConfig: AddNoteConfig;
  public isC2CPhoneEditingEnabled: boolean;
  public isRequestDetailsPhoneEditEnabled: boolean;
  public businessImpactConfirmed: boolean;
  public customerAvailabilityConfirmed: boolean;
  public isGetRequestPending: boolean;
  public constructor() {
    this.dirtyRequest = null;
    this.isEmailValid = false;
    this.isTitleValid = false;
    this.isPhoneNumberValid = false;
    this.isUpdatingAttachments = false;
    this.isUpdateAttachmentsError = false;
    this.isAddingNote = false;
    this.isAddNoteError = false;
    this.isUpdatingContacts = false;
    this.isUpdateContactsError = false;
    this.isRequestEditingEnabled = false;
    this.addNoteConfig = null;
    this.isC2CPhoneEditingEnabled = false;
    this.isRequestDetailsPhoneEditEnabled = false;
    this.businessImpactConfirmed = false;
    this.customerAvailabilityConfirmed = false;
    this.isGetRequestPending = false;
  }
}

export const initialRequestDetailsState = new RequestDetailsState();

export interface IRequestHistoryState {
  isSearchRequestsEnabledConsumer: boolean;
  isSearchRequestsEnabledCommercial: boolean;
  isSearchRequestsEnabledBA: boolean;
  searchRequestsRequestModel: SearchRequestsRequestModel;
  isSearchingRequests: boolean;
  allRequests: FreelanceRequest[];
  showRequestSubNav: boolean;
}

export class RequestHistoryState implements IRequestHistoryState {
  public isSearchRequestsEnabledConsumer: boolean;
  public isSearchRequestsEnabledCommercial: boolean;
  public isSearchRequestsEnabledBA: boolean;
  public searchRequestsRequestModel: SearchRequestsRequestModel;
  public isSearchingRequests: boolean;
  public allRequests: FreelanceRequest[];
  public showRequestSubNav: boolean;
  public constructor() {
    this.isSearchRequestsEnabledConsumer = false;
    this.isSearchRequestsEnabledCommercial = false;
    this.isSearchRequestsEnabledBA = false;
    this.searchRequestsRequestModel = new SearchRequestsRequestModel();
    this.isSearchingRequests = false;
    this.allRequests = null;
    this.showRequestSubNav = false;
  }
}

export const initialRequestHistoryState = new RequestHistoryState();

export interface IAttachmentsState {
  isUploadAttachmentInProgress: boolean;
  isAttachmentActionAborted: boolean;
  attachmentError: ErrorCodes;
  isAttachmentsEnabledConsumer: boolean;
  isAttachmentsEnabledCommercial: boolean;
  isAttachmentsEnabledBA: boolean;
  attachmentsConfig: AttachmentsConfig;
  cancelTokenIdsList: string[];
}

export class AttachmentsState implements IAttachmentsState {
  public isUploadAttachmentInProgress: boolean;
  public isAttachmentActionAborted: boolean;
  public attachmentError: ErrorCodes;
  public isAttachmentsEnabledConsumer: boolean;
  public isAttachmentsEnabledCommercial: boolean;
  public isAttachmentsEnabledBA: boolean;
  public attachmentsConfig: AttachmentsConfig;
  public cancelTokenIdsList: string[];
  public constructor() {
    this.isUploadAttachmentInProgress = false;
    this.isAttachmentActionAborted = false;
    this.attachmentError = null;
    this.isAttachmentsEnabledConsumer = false;
    this.isAttachmentsEnabledCommercial = false;
    this.isAttachmentsEnabledBA = false;
    this.attachmentsConfig = null;
    this.cancelTokenIdsList = [];
  }
}

export const initialAttachmentsState = new AttachmentsState();

export interface IOcvState {
  IsInitialized: boolean;
  IsEnabled: boolean;
  AppId: number;
  Environment: number;
  StylesUrl: string;
  IntlUrl: string;
  ShowScreenshot: boolean;
  ShowEmailAddress: boolean;
  ShowScreenRecording: boolean;
  ShowFileUpload: boolean;
  IsOcvVNextEnabled: boolean;
  IsSendAimIdEnabled: boolean;
}

export class OcvState implements IOcvState {
  public IsInitialized: boolean;
  public IsEnabled: boolean;
  public AppId: number;
  public Environment: number;
  public StylesUrl: string;
  public IntlUrl: string;
  public ShowScreenshot: boolean;
  public ShowEmailAddress: boolean;
  public ShowScreenRecording: boolean;
  public ShowFileUpload: boolean;
  public IsOcvVNextEnabled: boolean;
  public IsSendAimIdEnabled: boolean;

  public constructor() {
    this.IsInitialized = false;
    this.IsEnabled = false;
    this.AppId = 0;
    this.Environment = 1;
    this.StylesUrl = '';
    this.IntlUrl = '';
    this.ShowScreenshot = false;
    this.ShowEmailAddress = false;
    this.ShowScreenRecording = false;
    this.ShowFileUpload = false;
    this.IsOcvVNextEnabled = false;
    this.IsSendAimIdEnabled = false;
  }
}

export const initialOcvState = new OcvState();

export type ErrorCodesLabelsMap = { [key: number]: string };

export interface AttachmentsConfig {
  ChunkSizeInMB: number;
  AllowedFileExtensions: string[];
  ChunksPerBucket: number;
  UploadTokenRefreshInterval: number;
  MaxAttachmentsCount: number;
  MaxFileSizeInMB: number;
  FtsBaseUrl: string;
}

export interface AddNoteConfig {
  AddNoteTextFieldCharLimit: number;
  MaxAllowedNotesCount: number;
}

export interface IRequestFeedbackState {
  isRequestFeedbackEnabled: boolean;
  requestFeedbackCommentMaxLength: number;
  allRequestFeedback: RequestFeedbackSchema[];
}

export class RequestFeedbackState implements IRequestFeedbackState {
  public isRequestFeedbackEnabled: boolean;
  public requestFeedbackCommentMaxLength: number;
  public allRequestFeedback: RequestFeedbackSchema[];
  public constructor() {
    this.isRequestFeedbackEnabled = false;
    this.requestFeedbackCommentMaxLength = 1000;
    this.allRequestFeedback = [];
  }
}

export const initialRequestFeedbackState = new RequestFeedbackState();

export class ContactUsState implements IContactUsState {
  public SharedState: ISharedState;
  public AutoSuggestState: IAutoSuggestState;
  public SelfHelpAlchemyState: ISelfHelpAlchemyState;
  public ModalitySectionState: IModalitySectionState;
  public UserNoticeState: IUserNoticeState;
  public RequestDetailsState: IRequestDetailsState;
  public RequestHistoryState: IRequestHistoryState;
  public AttachmentsState: IAttachmentsState;
  public OcvState: IOcvState;
  public RequestFeedbackState: IRequestFeedbackState;
  public DafState: IDafState;

  public constructor() {
    this.SharedState = initialSharedState;
    this.AutoSuggestState = initialAutoSuggestState;
    this.SelfHelpAlchemyState = initialSelfHelpAlchemyState;
    this.ModalitySectionState = initialModalitySectionState;
    this.UserNoticeState = initialUserNoticeState;
    this.RequestHistoryState = initialRequestHistoryState;
    this.RequestDetailsState = initialRequestDetailsState;
    this.AttachmentsState = initialAttachmentsState;
    this.OcvState = initialOcvState;
    this.RequestFeedbackState = initialRequestFeedbackState;
    this.DafState = initialDafState;
  }
}

export interface ISessionData {
  userObjectId: string;
  authType: string;
  tenantId: string;
  sessionId: string;
}

export class SessionData implements ISessionData {
  public userObjectId: string;
  public authType: string;
  public tenantId: string;
  public sessionId: string;
  public constructor(userObjectId: string,
    authType: string,
    tenantId: string,
    sessionId: string) {
    this.userObjectId = userObjectId;
    this.authType = authType;
    this.tenantId = tenantId;
    this.sessionId = sessionId;
  }
}

export const initialContactState: IContactUsState = new ContactUsState();

// Combined states
export const initialAppState: IAppState = {
  GlobalState: initialGlobalState,
  NavState: initialNavState,
  ContactState: initialContactState,
};
